import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon } from 'lucide-react';
import { insidePages } from '../data/content';

/** Interior pages — swipe on touch, arrows and dots on desktop, tap to enlarge. */
export function InsidePagesCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  const total = insidePages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (slide) track.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const slides = Array.from(track.children) as HTMLElement[];
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      if (atEnd) {
        setActive(total - 1);
        return;
      }
      let closest = 0;
      slides.forEach((slide, index) => {
        if (Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[closest].offsetLeft - track.scrollLeft)) {
          closest = index;
        }
      });
      setActive(closest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, [total]);
  useEffect(() => {
    if (zoomed === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setZoomed(null);
      if (event.key === 'ArrowRight') setZoomed(current => current === null ? null : (current + 1) % total);
      if (event.key === 'ArrowLeft') setZoomed(current => current === null ? null : (current - 1 + total) % total);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [zoomed, total]);
  return <div className="relative mx-auto max-w-5xl" aria-roledescription="carousel" aria-label="Pages from PMU Instructor Ready" data-id="element-19" data-mp="3e2jzta3lj3wk">
      <div ref={trackRef} className="no-scrollbar relative flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2" data-id="element-20" data-mp="2qeagqfxrou94">
        {insidePages.map((page, index) => <figure key={page.src} className="w-[86%] flex-none snap-start sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)]" aria-roledescription="slide" aria-label={`${index + 1} of ${total}: ${page.label}`} data-id="element-21" data-mp="3030sgjilzwlb" data-mpi={index}>
            <button type="button" onClick={() => setZoomed(index)} aria-label={`Enlarge: ${page.label}`} className="group relative block w-full overflow-hidden rounded-[18px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta focus-visible:ring-offset-2" data-id="element-22" data-mp="27kwvx3nrq478">
              <img src={page.src} alt={`${page.label} — ${page.caption}, interior page of PMU Instructor Ready`} width={1080} height={1350} loading={index < 2 ? 'eager' : 'lazy'} decoding="async" draggable={false} className="block aspect-[1080/1350] h-auto w-full select-none rounded-[18px] border border-brand-charcoal/10 bg-white object-contain shadow-[0_30px_60px_-36px_rgba(90,70,40,0.5)]" data-id="element-23" data-mp="95z2y7p81ti7" />
              <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-brand-charcoal shadow-[0_6px_16px_-8px_rgba(42,37,32,0.5)] transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-24" data-mp="wsxfwve4ujbd">
                <ZoomInIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-25" data-mp="pruy18zxvvk" />
              </span>
            </button>
            <figcaption className="mt-4 px-1" data-id="element-26" data-mp="3s34g77ir63t8">
              <span className="block font-display text-[1.25rem] font-bold leading-tight text-brand-charcoal" data-id="element-27" data-mp="3o2c6kd5yxwwf">
                {page.label}
              </span>
              <span className="mt-1 block font-body text-[12px] text-brand-charcoal/55" data-id="element-28" data-mp="3j4ssped6nm50">{page.caption}</span>
            </figcaption>
          </figure>)}
      </div>

      <ArrowButton side="left" label="Previous page" onClick={() => goTo(active - 1)} data-id="element-29" data-mp="fnkceyy3suce">
        <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-30" data-mp="amwju8u885g2" />
      </ArrowButton>
      <ArrowButton side="right" label="Next page" onClick={() => goTo(active + 1)} data-id="element-31" data-mp="5p4iko7oz6z7">
        <ChevronRightIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-32" data-mp="2ve6jhjrfzvtf" />
      </ArrowButton>

      <div className="mt-6 flex items-center justify-center gap-2.5" data-id="element-33" data-mp="2qe0z8oni5bld">
        {insidePages.map((page, index) => <button key={page.src} type="button" aria-label={`Show: ${page.label}`} aria-current={index === active} onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta focus-visible:ring-offset-2 ${index === active ? 'w-8 bg-brand-cta' : 'w-2 bg-brand-charcoal/20 hover:bg-brand-charcoal/35'}`} data-id="element-34" data-mp="2gbolc7od2jf7" data-mpi={index} />)}
      </div>

      <AnimatePresence data-id="element-35" data-mp="38zb08c31hahd">
        {zoomed !== null && <motion.div role="dialog" aria-modal="true" aria-label={insidePages[zoomed].label} className="fixed inset-0 z-50 flex items-center justify-center bg-brand-charcoal/85 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={() => setZoomed(null)} data-id="element-36" data-mp="38zu973apooj9">
            <motion.img key={insidePages[zoomed].src} src={insidePages[zoomed].src} alt={`${insidePages[zoomed].label} — enlarged interior page`} className="max-h-[88vh] w-auto max-w-full rounded-[14px] bg-white object-contain" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={event => event.stopPropagation()} data-id="element-37" data-mp="16l3uum5sfxtm" />
            <button type="button" aria-label="Close" onClick={() => setZoomed(null)} className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white text-brand-charcoal focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-butter" data-id="element-38" data-mp="1z99awvlwcyn4">
              <XIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-39" data-mp="2rx0xndivqs60" />
            </button>
            <button type="button" aria-label="Previous page" onClick={event => {
          event.stopPropagation();
          setZoomed((zoomed - 1 + total) % total);
        }} className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-charcoal focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-butter" data-id="element-40" data-mp="2wtj2ede7mqic">
              <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-41" data-mp="205cpz4b1qtti" />
            </button>
            <button type="button" aria-label="Next page" onClick={event => {
          event.stopPropagation();
          setZoomed((zoomed + 1) % total);
        }} className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-charcoal focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-butter" data-id="element-42" data-mp="2476op71olnr1">
              <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-43" data-mp="21mjqmbai6gom" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function ArrowButton({
  side,
  label,
  onClick,
  children
}: {
  side: 'left' | 'right';
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className={`absolute top-[calc(50%-4rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-brand-charcoal/10 bg-white text-brand-charcoal shadow-[0_10px_24px_-12px_rgba(42,37,32,0.4)] transition-[background-color,transform] duration-150 ease-out hover:bg-brand-ivory active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta md:flex ${side === 'left' ? '-left-6' : '-right-6'}`} data-id="element-44" data-mp="1mwcjustuym3r">
      {children}
    </button>;
}